"use client";

// ── The graded-solution draft (`EX-PUB01`, shared from `EX-PUB08`) ──────────
// Lifted out of `ProblemWorkspaceClient` unchanged the day `/judge/[slug]`
// gained a second caller. A contest problem is the same editor pointed at a
// different route, and "seed from the first graded runtime, then persist what
// the user typed" copied twice is two places for the two surfaces to drift -
// including the one that matters mid-contest, where losing a draft to a reload
// costs a contestant the round.

import { useCallback, useEffect, useRef, useState } from "react";

import { getRuntimeDefaultSourceCode } from "@/features/quiz/lib/coding-task-format";

export type SolutionDraft = {
  languageKey: string;
  languageVersion: string;
  sourceCode: string;
};

/** One `(language, version)` the graded runner will accept. */
export type AllowedRuntime = { languageKey: string; languageVersion: string };

function readStoredDraft(storageKey: string): SolutionDraft | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SolutionDraft>;
    if (
      typeof parsed.languageKey === "string" &&
      typeof parsed.languageVersion === "string" &&
      typeof parsed.sourceCode === "string"
    ) {
      return parsed as SolutionDraft;
    }
  } catch {
    // A corrupt draft is just no draft.
  }
  return null;
}

function storeDraft(storageKey: string, draft: SolutionDraft): void {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(draft));
  } catch {
    // Quota or private mode - losing the convenience is fine.
  }
}

/**
 * The runtime a fresh draft opens on: the preferred one when the runner offers
 * it, the same language at another version when only the version is missing,
 * else the catalogue's first.
 */
export function seedRuntime(
  allowedRuntimes: AllowedRuntime[],
  preferred: AllowedRuntime | null,
): AllowedRuntime | undefined {
  if (preferred) {
    const exact = allowedRuntimes.find(
      (runtime) =>
        runtime.languageKey === preferred.languageKey &&
        runtime.languageVersion === preferred.languageVersion,
    );
    if (exact) return exact;
    const sameLanguage = allowedRuntimes.find((runtime) => runtime.languageKey === preferred.languageKey);
    if (sameLanguage) return sameLanguage;
  }
  return allowedRuntimes[0];
}

/**
 * The editor's draft for one problem, persisted under `storageKey`.
 *
 * First mount: a stored draft wins; otherwise {@link seedRuntime} and its
 * starter code. Never overwrites what the user has already typed.
 *
 * `preferred` (`EX-PUB21`) is read when the draft is seeded, so a caller can
 * pass a reader of browser storage without reading it during a server render.
 * Absent, the first graded runtime is used, as before.
 *
 * `allowedRuntimes` must be referentially stable (memoise it at the call site)
 * - it is an effect dependency, and a fresh array every render would re-seed.
 */
export function useSolutionDraft(
  storageKey: string,
  allowedRuntimes: AllowedRuntime[],
  options: { preferred?: () => AllowedRuntime | null } = {},
) {
  const [draft, setDraft] = useState<SolutionDraft | null>(null);

  useEffect(() => {
    if (draft) return;
    const stored = readStoredDraft(storageKey);
    if (stored) {
      setDraft(stored);
      return;
    }
    const seed = seedRuntime(allowedRuntimes, options.preferred?.() ?? null);
    if (seed) {
      setDraft({
        languageKey: seed.languageKey,
        languageVersion: seed.languageVersion,
        sourceCode: getRuntimeDefaultSourceCode(seed.languageKey),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedRuntimes, storageKey]);

  const persistTimer = useRef<number | null>(null);
  const pushDraft = useCallback(
    (next: SolutionDraft) => {
      setDraft(next);
      if (persistTimer.current) window.clearTimeout(persistTimer.current);
      persistTimer.current = window.setTimeout(() => storeDraft(storageKey, next), 400);
    },
    [storageKey],
  );

  return { draft, pushDraft };
}
